import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  Clock,
  MapPin,
  CreditCard,
  Download,
  Printer,
  HelpCircle,
  ChevronRight,
  Box,
  Home,
  Star,
  MessageSquare,
  FileText
} from 'lucide-react';
import { formatINR } from '../utils/currency';

interface Order {
  _id: string;
  orderNumber: string;
  orderDate: string;
  status: string;
  paymentStatus: string;
  paymentMethod: string;
  totalAmount: number;
  subtotal: number;
  shippingCost: number;
  tax: number;
  discount: number;
  items: Array<{
    _id: string;
    product: {
      _id: string;
      name: string;
      images: Array<{ url: string }>;
      slug: string;
    };
    quantity: number;
    price: number;
    total: number;
  }>;
  shippingAddress: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    phone: string;
  };
  timeline: Array<{
    status: string;
    label: string;
    description: string;
    timestamp: string;
    completed: boolean;
    icon: string;
  }>;
  tracking?: {
    carrier: string;
    trackingNumber: string;
    trackingUrl: string;
  };
  billFile?: string;
  billUrl?: string;
}

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setFetchError(null);

      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const response = await fetch(`${API_URL}/orders/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Transform order data to handle nested totals object
          const orderData = data.data;
          
          // Transform createdAt to orderDate
          if (orderData.createdAt && !orderData.orderDate) {
            orderData.orderDate = orderData.createdAt;
          }
          
          if (orderData.totals) {
            orderData.subtotal = orderData.totals.subtotal || 0;
            orderData.shippingCost = orderData.totals.shipping || 0;
            orderData.tax = orderData.totals.tax || 0;
            orderData.totalAmount = orderData.totals.grandTotal || 0;
            orderData.discount = orderData.totals.discount || 0;
          }
          // Transform payment object to flat fields
          if (orderData.payment) {
            orderData.paymentMethod = orderData.payment.method || orderData.paymentMethod || 'N/A';
            orderData.paymentStatus = orderData.payment.status || orderData.paymentStatus || 'pending';
          }
          // Add bill URL if billFile exists
          if (orderData.billFile) {
            const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';
            orderData.billUrl = `${SERVER_URL}/uploads/bills/${orderData.billFile}`;
          }
          setOrder(orderData);
          setLoading(false);
          return;
        }
      }

      const errBody = await response.json().catch(() => ({}));
      const message =
        errBody.message ||
        (response.status === 404 ? 'Order not found' : 'Unable to load order details');
      console.error('[OrderDetail] fetch failed', response.status, message);
      setOrder(null);
      setFetchError(message);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      setOrder(null);
      setFetchError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    try {
      // TODO: API call to cancel order
      console.log('Cancelling order with reason:', cancelReason);
      setShowCancelModal(false);
      // Refresh order details
      fetchOrderDetails();
    } catch (error) {
      console.error('Failed to cancel order:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      packed: 'bg-indigo-100 text-indigo-800',
      shipped: 'bg-purple-100 text-purple-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTimelineIcon = (icon: string, completed: boolean) => {
    const iconClass = completed ? 'text-blue-600' : 'text-gray-400';
    switch (icon) {
      case 'check':
        return <CheckCircle className={`w-6 h-6 ${iconClass}`} />;
      case 'processing':
        return <Clock className={`w-6 h-6 ${iconClass}`} />;
      case 'box':
        return <Box className={`w-6 h-6 ${iconClass}`} />;
      case 'truck':
        return <Truck className={`w-6 h-6 ${iconClass}`} />;
      case 'delivery':
        return <Package className={`w-6 h-6 ${iconClass}`} />;
      case 'home':
        return <Home className={`w-6 h-6 ${iconClass}`} />;
      default:
        return <CheckCircle className={`w-6 h-6 ${iconClass}`} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {fetchError ? 'Could not load order' : 'Order not found'}
          </h2>
          <p className="text-gray-600 mb-6">{fetchError || "The order you're looking for doesn't exist."}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {fetchError && (
              <button
                type="button"
                onClick={() => fetchOrderDetails()}
                className="px-5 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
              >
                Try again
              </button>
            )}
            <Link to="/orders" className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 inline-block">
              Back to Orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/orders"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Orders
          </Link>
          {order.billUrl && (
            <div className="flex space-x-2">
              <button 
                onClick={() => window.print()}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Printer className="w-4 h-4" />
                <span className="hidden sm:inline">Print</span>
              </button>
              <a 
                href={order.billUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Invoice</span>
              </a>
            </div>
          )}
        </div>

        {/* Order Summary Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Order #{order.orderNumber}</h1>
              <p className="text-gray-600">
                Placed on {new Date(order.orderDate).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-3">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                {order.status.replace('_', ' ').toUpperCase()}
              </span>
              {order.status !== 'cancelled' && order.status !== 'delivered' && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
                >
                  Cancel Order
                </button>
              )}
            </div>
          </div>

          {/* Tracking Info */}
          {order.tracking && order.status !== 'pending' && order.status !== 'cancelled' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center space-x-3">
                  <Truck className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">
                      {order.tracking.carrier}
                    </p>
                    <p className="text-sm text-blue-700">
                      Tracking ID: {order.tracking.trackingNumber}
                    </p>
                  </div>
                </div>
                <a
                  href={order.tracking.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 sm:mt-0 inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                >
                  Track Package
                  <ChevronRight className="w-4 h-4 ml-1" />
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Order Timeline */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-6">Order Status</h2>
          <div className="relative">
            {(order.timeline || []).map((event, index) => (
              <div key={event.status} className="flex mb-8 last:mb-0">
                {/* Timeline Line */}
                {index !== (order.timeline || []).length - 1 && (
                  <div
                    className={`absolute left-3 top-8 bottom-0 w-0.5 h-full -mb-8 ${
                      event.completed ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                )}
                
                {/* Icon */}
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                  event.completed ? 'bg-blue-600' : 'bg-gray-300'
                }`}>
                  {getTimelineIcon(event.icon, event.completed)}
                </div>

                {/* Content */}
                <div className="ml-4 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className={`font-semibold ${
                        event.completed ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {event.label}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {event.description}
                      </p>
                    </div>
                    {event.timestamp && (
                      <p className="text-sm text-gray-500 mt-2 sm:mt-0">
                        {new Date(event.timestamp).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Items */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Order Items</h2>
              <div className="space-y-4">
                {(order.items || []).map((item) => (
                  <div
                    key={item._id}
                    className="flex space-x-4 py-4 border-b last:border-0"
                  >
                    <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={item.product?.images?.[0]?.url || '/placeholder-product.jpg'}
                        alt={item.product?.name || 'Product'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <Link
                        to={`/product/${item.product.slug}`}
                        className="font-semibold text-gray-900 hover:text-blue-600"
                      >
                        {item.product.name}
                      </Link>
                      <p className="text-sm text-gray-600 mt-1">
                        Qty: {item.quantity}
                      </p>
                      <p className="font-semibold text-gray-900 mt-2">
                        {formatINR(item.total)}
                      </p>
                    </div>
                    <div className="flex flex-col space-y-2">
                      {order.status === 'delivered' && (
                        <>
                          <button className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700">
                            <Star className="w-4 h-4" />
                            <span>Rate</span>
                          </button>
                          <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-700">
                            <MessageSquare className="w-4 h-4" />
                            <span>Review</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Invoice/Bill Download */}
            {order.billUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  Invoice / Bill
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your official invoice is ready for download
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href={order.billUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Bill</span>
                  </a>
                  <button
                    onClick={() => window.print()}
                    className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Print</span>
                  </button>
                </div>
              </div>
            )}

            {/* Need Help */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Need Help?</h3>
              <div className="grid grid-cols-1 gap-4">
                <Link
                  to="/help"
                  className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                  <span>Contact Support</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="space-y-6">
            {/* Shipping Address */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Shipping Address
              </h3>
              <div className="text-gray-600">
                <p className="font-medium text-gray-900">
                  {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                </p>
                <p className="mt-1">{order.shippingAddress.address}</p>
                <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}</p>
                <p className="mt-2">Phone: {order.shippingAddress.phone}</p>
              </div>
            </div>

            {/* Payment Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Payment Information
              </h3>
              <div className="text-gray-600">
                <p className="font-medium text-gray-900">{order.paymentMethod}</p>
                <p className="mt-1">
                  Status: <span className={`font-medium ${
                    order.paymentStatus === 'paid' ? 'text-green-600' :
                    order.paymentStatus === 'pending' ? 'text-yellow-600' :
                    order.paymentStatus === 'failed' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>{order.paymentStatus}</span>
                </p>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>
              <div className="space-y-3 text-gray-600">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatINR(order.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>{order.shippingCost === 0 ? 'FREE' : formatINR(order.shippingCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax</span>
                  <span>{formatINR(order.tax)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatINR(order.discount)}</span>
                  </div>
                )}
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between font-bold text-lg text-gray-900">
                    <span>Total</span>
                    <span>{formatINR(order.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Cancel Order</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel this order? This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for cancellation (optional)
              </label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label="Select cancellation reason"
              >
                <option value="">Select a reason</option>
                <option value="changed_mind">Changed my mind</option>
                <option value="found_cheaper">Found cheaper elsewhere</option>
                <option value="ordered_by_mistake">Ordered by mistake</option>
                <option value="delivery_too_slow">Delivery is too slow</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleCancelOrder}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Cancel Order
              </button>
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 border border-gray-300 py-3 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Keep Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
